import FormatCurrency from 'components/Common/FormatCurrency'
import Image from 'components/Common/Image/Image'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FullWidthCarousel from 'components/Luxkit/Carousel/FullWidthCarousel'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import GlobalSearchContainerPane, { GlobalSearchContainerPaneContent } from 'components/Search/GlobalSearch/GlobalSearchContainerPane'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { fareCabins, fareTypes } from 'constants/flight'
import { FLASH_HOTELS_AND_TAO_OFFERS_TYPES } from 'constants/offerList'
import GeoContext from 'contexts/geoContext'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import useOfferList from 'hooks/Offers/useOfferList'
import { take, unique, without } from 'lib/array/arrayUtils'
import { mapAirportToAirportLocation } from 'lib/flights/flightUtils'
import { rem } from 'polished'
import React, { useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'
import FlightDealTile from '../FlightMerchandising/FlightDealTile'
import FlightSearchWidget, { FlightSearchWidgetSearchData } from '../FlightsSearch/SearchWidget/FlightSearchWidget'
import FlightDealContentCountdown from './FlightDealContentCountdown'
import FlightDealUSPs from './FlightDealUSPs'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import Typography from 'components/Luxkit/Typography/Typography'
import { useElementHeight } from 'hooks/useElementSize'
import { usePlaceByName } from 'hooks/usePlace'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import SupportTile from 'components/Common/SupportTile/SupportTile'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import config from 'constants/config'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import uuidV4 from 'lib/string/uuidV4Utils'
import { useAppSelector } from 'hooks/reduxHooks'
import { isMultiCityEnabled } from 'selectors/flightsSelectors'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'

const FlightDealHero = styled.div`
  padding-top: ${rem(70)};
  position: relative;
  background-color: ${props => props.theme.palette.neutral.default.one};
  padding-bottom: calc(var(--search-offset, 0) + ${rem(16)});

  ${mediaQueryUp.desktop} {
    padding-bottom: calc(var(--search-offset, 0) + ${rem(40)});
  }
`

const HeroImage = styled(ResponsiveImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));
    pointer-events: none;
  }
`

const Container = styled(Pane)`
  position: relative;
  z-index: 1;
`

const FlightDealSearch = styled(LayoutContainer)`
  transform: translateY(-50%);
`

const ContentSection = styled(Pane)`
  padding: ${rem(40)} 0;
`

const LargeContentSection = styled(Pane)`
  padding: ${rem(56)} 0;
`

const LogoContainer = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(8)};
`

const LogoImage = styled(Image)`
  max-width: ${rem(150)};

  ${mediaQueryUp.tablet} {
    max-width: ${rem(200)};
  }
`

interface Props {
  flightDeal: App.FlightDeal;
}

function FlightDealContent(props: Props) {
  const { flightDeal } = props

  let [originFlightDeals] = useFlightDeals('all', { originAirportCode: flightDeal.originAirportCode })
  let [fallbackFlightDeals] = useFlightDeals('all')
  originFlightDeals = without(originFlightDeals, flightDeal)
  fallbackFlightDeals = without(fallbackFlightDeals, flightDeal)
  const otherFlightDeals = useMemo(() => {
    return originFlightDeals.length > 0 ? originFlightDeals : fallbackFlightDeals
  }, [originFlightDeals, fallbackFlightDeals])
  const destinationPlace = usePlaceByName(flightDeal.destinationName)
  const multiCityEnabled = useAppSelector(isMultiCityEnabled)

  const alternativeClassFlightDeals = useMemo(() => {
    return otherFlightDeals.filter(deal => deal.originAirportCode === flightDeal.originAirportCode &&
      deal.destinationAirportCode === flightDeal.destinationAirportCode &&
      deal.carrier.code === flightDeal.carrier.code &&
      deal.groupId === flightDeal.groupId,
    )
  }, [flightDeal, otherFlightDeals])

  const [dealFareType, dealClass] = useMemo(() => ([
    fareTypes.find(type => type.value === flightDeal.fareType),
    fareCabins.find(type => type.value === flightDeal.fareClass),
  ]), [flightDeal])

  const { airports } = useContext(GeoContext)

  const searchData = useMemo((): Partial<FlightSearchWidgetSearchData> => {
    const departure = airports.find(airport => airport.code === flightDeal.originAirportCode)
    const arrival = airports.find(airport => airport.code === flightDeal.destinationAirportCode)

    return {
      fareType: dealFareType,
      fareCabin: dealClass,
      filterAirlines: [flightDeal.carrier.code],
      flightDealId: flightDeal.id,
      flights: [{
        id: uuidV4(),
        departureAirport: departure ? mapAirportToAirportLocation(departure) : undefined,
        arrivalAirport: arrival ? mapAirportToAirportLocation(arrival) : undefined,
      }],
    }
  }, [airports, flightDeal, dealClass, dealFareType])

  const destinationOfferList = useOfferList({
    offerTypes: FLASH_HOTELS_AND_TAO_OFFERS_TYPES,
    destinationId: destinationPlace?.id,
  }, { disabled: !destinationPlace })
  const fallbackOfferList = useOfferList({
    offerTypes: FLASH_HOTELS_AND_TAO_OFFERS_TYPES,
  }, { disabled: !destinationPlace })
  const isSaleActive = useMemo(() => new Date() < new Date(flightDeal.salesEndDate), [flightDeal.salesEndDate])

  const finalOfferList: App.OfferList = useMemo(() => {
    return {
      error: destinationOfferList.error || fallbackOfferList.error,
      fetching: destinationOfferList.fetching,
      key: 'flightDealOffers',
      offerIds: take(
        unique([
          ...take(destinationOfferList.offerIds, 16),
          ...take(fallbackOfferList.offerIds, 16),
        ]),
        16),
    }
  }, [destinationOfferList, fallbackOfferList])

  const dateFormatter = useIntlDateFormatter()

  const travelPeriodsLabel = useMemo(() => {
    return flightDeal.travelPeriods.map((period) => {
      return `${dateFormatter(period.departureDate, INTL_DMY_CASUAL_SHORT_FORMAT)} - ${dateFormatter(period.arrivalDate, INTL_DMY_CASUAL_SHORT_FORMAT)}`
    }).join(' · ')
  }, [dateFormatter, flightDeal.travelPeriods])

  const showTravelPeriods = isSaleActive && flightDeal.travelPeriods.length > 0

  const containerRef = useRef<HTMLDivElement>(null)
  const searchHeight = useElementHeight(containerRef)

  const searchOffset = searchHeight ? (searchHeight / 2) : undefined

  return <Pane>
    <FlightDealHero style={{ '--search-offset': rem(searchOffset ?? 0) } as any}>
      {!!flightDeal.heroImage?.id && <HeroImage
        id={flightDeal.heroImage.id}
        fit="center"
      />}
      <LayoutContainer style={{ transform: 'translateZ(0)' }}>
        <Group direction="vertical" horizontalAlign="center" gap={40}>
          <Group direction="vertical" horizontalAlign="center" gap={32}>
            <Group direction="vertical" horizontalAlign="center" gap={12}>
              <LogoContainer>
                {flightDeal.carrier.logo && <LogoImage width="200px" image={flightDeal.carrier.logo} dpr={2} enhancedFormats={false} />}
              </LogoContainer>
              {!flightDeal.carrier.logo && <Heading variant="heading4" colour="neutral-eight">{flightDeal.carrier.name}</Heading> }
              <Heading variant="heading1" align="center" colour="neutral-eight">
                {flightDeal.originName} to {flightDeal.destinationName}{' '}
                from <FormatCurrency value={flightDeal.perAdultPrice} />{' '}
                <Typography as="i" format="lowercase">{dealFareType?.label}*</Typography>
              </Heading>
              <VerticalSpacer gap={12}>
                <BodyText variant="medium" align="center" colour="neutral-eight" format="titlecase">
                  Class: {dealClass?.label}{flightDeal.travelDays && ` · Departs: ${flightDeal.travelDays}`}
                </BodyText>

                <VerticalSpacer gap={8}>
                  {showTravelPeriods && <VerticalSpacer gap={4}>
                    <CaptionTextBlock variant="small" colour="neutral-eight" weight="bold" align="center">
                      Travel between
                    </CaptionTextBlock>

                    <Group direction="vertical">
                      <BodyTextBlock variant="medium" colour="neutral-eight" align="center">
                        {travelPeriodsLabel}
                      </BodyTextBlock>
                    </Group>
                  </VerticalSpacer>}

                  {flightDeal.dealDescription && <BodyTextBlock variant="medium" colour="neutral-eight" align="center">
                    {flightDeal.dealDescription}
                  </BodyTextBlock>}
                </VerticalSpacer>
              </VerticalSpacer>
            </Group>
            <Group direction="vertical" horizontalAlign="center" gap={16}>
              <UrgencyLabel variant="critical2">
                {isSaleActive ? 'Offer ends in' : 'This sale has ended'}{' '}
                <FlightDealContentCountdown deal={flightDeal}/>
              </UrgencyLabel>
              <Group
                direction="vertical"
                horizontalAlign="center"
                tabletDirection="horizontal"
                wrap="wrap"
                gap={8}
              >
                {alternativeClassFlightDeals.map((deal, index) => <React.Fragment key={index}>
                  {index !== 0 && <CSSBreakpoint min="tablet">
                    <BodyText variant="small" colour="neutral-eight">·</BodyText>
                  </CSSBreakpoint>}
                  <TextLink
                    size="small"
                    variant="ghost"
                    to={`/flight/deal/${deal.id}`}
                    target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
                  >
                    View <FormatCurrency value={deal.perAdultPrice} /> {deal.fareClass} class deal
                  </TextLink>
                </React.Fragment>)}
              </Group>
            </Group>
          </Group>
        </Group>
      </LayoutContainer>
    </FlightDealHero>
    <Container
      style={{ height: searchOffset }}
    >
      <FlightDealSearch ref={containerRef}>
        <GlobalSearchContainerPane displayMobile>
          <GlobalSearchContainerPaneContent>
            <FlightSearchWidget
              searchData={searchData}
              showMultiCity={multiCityEnabled}
            />
          </GlobalSearchContainerPaneContent>
        </GlobalSearchContainerPane>
      </FlightDealSearch>
    </Container>

    <ContentSection>
      <LayoutContainer>
        <SupportTile
          title="Call us to find out more or book this offer today"
          description="Our dedicated flights team are available anytime, 24 hours, 7 days a week."
        />
      </LayoutContainer>
    </ContentSection>

    {isSaleActive && <LargeContentSection>
      <VerticalSpacer gap={32}>
        <LayoutContainer>
          <Heading variant="heading2">
            Bundle hotel + flights to <i>save</i>
          </Heading>
        </LayoutContainer>
        <OfferListCarousel
          overrideOfferList={finalOfferList}
        />
      </VerticalSpacer>
    </LargeContentSection>}
    {otherFlightDeals.length > 0 && <ContentSection>
      <VerticalSpacer gap={32}>
        <LayoutContainer>
          <Heading variant="heading2">
            Explore more <i>limited-time</i> offers {originFlightDeals.length > 0 && 'from ' + flightDeal.originName}
          </Heading>
        </LayoutContainer>
        <FullWidthCarousel gap={20} width="75vw" desktopWidth="320px" snap="start">
          {otherFlightDeals.map((deal, index) => (
            <FlightDealTile key={deal.id} flightDeal={deal} position={index} />
          ))}
        </FullWidthCarousel>
      </VerticalSpacer>
    </ContentSection>}
    <LayoutContainer>
      <ContentSection>
        <VerticalSpacer gap={32}>
          <Heading variant="heading2">
            Why you should <i>book</i> with us
          </Heading>
          <FlightDealUSPs />
        </VerticalSpacer>
      </ContentSection>
      <ContentSection>
        <VerticalSpacer gap={8}>
          <Heading variant="heading6">Terms & Conditions</Heading>
          <BodyText variant="small" colour="neutral-three">
            {flightDeal.termsAndConditions}
          </BodyText>
        </VerticalSpacer>
      </ContentSection>
    </LayoutContainer>
  </Pane>
}

export default FlightDealContent
